import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Settings as SettingsIcon, Palette, Eye, Home, ArrowRight, Shield, Key, Bell, User, Download, Trash2, ShoppingCart, Package } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { useManagementMode } from '@/hooks/useManagementMode';
import { MainLayout } from '@/components/layouts/MainLayout';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { FontSizeControl } from '@/components/ui/font-size-control';
import { ShowPricesToggle } from '@/components/caisse/ShowPricesToggle';
import { useUserSettings } from '@/hooks/useUserSettings';
import { useNotifications } from '@/hooks/useNotifications';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
export const Settings: React.FC = () => {
  const {
    userRole,
    currentCompany
  } = useCompany();
  const {
    settings,
    updateSettings
  } = useUserSettings();
  const {
    mode,
    isCaisseMode,
    isRetraitMode,
    updateMode,
    loading: modeLoading
  } = useManagementMode();
  const navigate = useNavigate();
  const { success, error } = useNotifications();
  
  // États pour les formulaires
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: ''
  });
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Gérer le changement de mot de passe
  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      error({ title: 'Erreur', description: 'Les mots de passe ne correspondent pas' });
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      error({ title: 'Erreur', description: 'Le mot de passe doit contenir au moins 6 caractères' });
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (updateError) throw updateError;
      
      success({ title: 'Succès', description: 'Mot de passe mis à jour avec succès' });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      error({ title: 'Erreur', description: 'Impossible de mettre à jour le mot de passe' });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Exporter les données
  const handleExportData = async () => {
    try {
      // Ici vous pourriez implémenter l'export des données de l'utilisateur
      success({ title: 'Export', description: 'Fonctionnalité d\'export bientôt disponible' });
    } catch (err) {
      error({ title: 'Erreur', description: 'Impossible d\'exporter les données' });
    }
  };

  // Supprimer le compte
  const handleDeleteAccount = async () => {
    try {
      // Ici vous pourriez implémenter la suppression du compte
      error({ title: 'Attention', description: 'Cette fonctionnalité nécessite une confirmation par email' });
    } catch (err) {
      error({ title: 'Erreur', description: 'Impossible de supprimer le compte' });
    }
  };

  // Gérer le changement de mode avec navigation
  const handleModeChange = async (newMode: 'caisse' | 'retrait') => {
    const success = await updateMode(newMode);
    if (success) {
      // Rediriger vers la page appropriée après changement de mode
      setTimeout(() => {
        if (newMode === 'caisse') {
          navigate('/caisse');
        } else if (newMode === 'retrait') {
          navigate('/retrait');
        }
      }, 500); // Petit délai pour laisser le toast s'afficher
    }
  };

  const goToHomePage = () => {
    // Adapter selon le mode de gestion
    let target = '/dashboard';
    if (isCaisseMode) {
      target = '/caisse';
    } else if (isRetraitMode) {
      target = '/retrait';
    } else if (settings.homePage && userRole === 'admin') {
      target = `/${settings.homePage}`;
    } else if (userRole !== 'admin') {
      target = '/caisse'; // Fallback pour les non-admin
    }
    navigate(target);
  };
  return <MainLayout title="Paramètres">
      <div className="space-y-6">
        {/* En-tête */}
        <Card role="region" aria-labelledby="settings-header">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-br from-primary/10 to-primary/20 transition-transform duration-200 hover:scale-105">
                <SettingsIcon className="w-6 h-6 text-primary" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium mb-1 text-primary/70">
                  Configuration
                </p>
                <h2 id="settings-header" className="text-xl font-bold text-primary">
                  Paramètres de l'application
                </h2>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3">
              Configurez votre expérience utilisateur selon vos préférences.
            </p>
          </CardHeader>
        </Card>

        {/* Paramètres d'affichage */}
        <Card role="region" aria-labelledby="appearance-section">
          <CardHeader>
            <CardTitle id="appearance-section" className="flex items-center gap-2">
              <Palette className="w-5 h-5" aria-hidden="true" />
              Apparence
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium">Thème</h4>
                <p className="text-sm text-muted-foreground">
                  Choisissez entre le mode clair et sombre
                </p>
              </div>
              <ThemeToggle showLabel />
            </div>
          </CardContent>
        </Card>

        {/* Analyse IA du Dashboard */}
        {userRole === 'admin' && <Card role="region" aria-labelledby="ai-analysis-section">
          <CardHeader>
            <CardTitle id="ai-analysis-section" className="flex items-center gap-2">
              <Bell className="w-5 h-5" aria-hidden="true" />
              Analyse IA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium">Animations IA</h4>
                <p className="text-sm text-muted-foreground">
                  Activer ou désactiver les animations dans les panneaux d'analyse IA
                </p>
              </div>
              <Switch
                checked={(() => {
                  const saved = localStorage.getItem('ai-insights-animations');
                  return saved !== 'false';
                })()}
                onCheckedChange={(checked) => {
                  localStorage.setItem('ai-insights-animations', String(checked));
                  window.location.reload();
                }}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium">Heure d'analyse automatique</h4>
                <p className="text-sm text-muted-foreground">
                  L'analyse IA sera effectuée une fois par jour à cette heure (limite: 1 analyse/24h)
                </p>
              </div>
              <Select 
                value={String(settings.dashboardAnalysisHour || 9)} 
                onValueChange={(value) => updateSettings({ dashboardAnalysisHour: parseInt(value) })}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {i.toString().padStart(2, '0')}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
              💡 L'analyse sera déclenchée automatiquement après cette heure lors de votre prochaine connexion
            </div>
          </CardContent>
        </Card>}

        {/* Taille de police */}
        <Card role="region" aria-labelledby="accessibility-section">
          <CardHeader>
            <CardTitle id="accessibility-section" className="flex items-center gap-2">
              <Eye className="h-4 w-4" aria-hidden="true" />
              Accessibilité
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium">Taille de police</h4>
                <p className="text-sm text-muted-foreground">
                  Ajustez la taille du texte pour une meilleure lisibilité
                </p>
              </div>
              <FontSizeControl showLabel />
            </div>
          </CardContent>
        </Card>

        {/* Page d'accueil - Masquer en mode spécialisé */}
        {!isCaisseMode && !isRetraitMode && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5" />
                Navigation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h4 className="text-sm font-medium">Page d'accueil</h4>
                  <p className="text-sm text-muted-foreground">
                    Choisissez la page qui s'ouvre par défaut après connexion
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={settings.homePage} onValueChange={(value: 'dashboard' | 'caisse' | 'retrait') => updateSettings({
                homePage: value
              })}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dashboard">Tableau de bord</SelectItem>
                      <SelectItem value="caisse">Caisse</SelectItem>
                      <SelectItem value="retrait">Retrait</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={goToHomePage} variant="outline" size="sm" className="flex items-center gap-2">
                    Aller <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>}

        {/* Mode de gestion */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5" />
              Mode de gestion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="text-sm font-medium mb-2">Mode actuel: {mode === 'caisse' ? 'Caisse' : mode === 'retrait' ? 'Retrait' : 'Non défini'}</h4>
              <p className="text-sm text-muted-foreground mb-4">
                Changez de mode de gestion selon vos besoins. Le mode caisse optimise l'interface pour les ventes, 
                tandis que le mode retrait se concentre sur la gestion des sorties de stock.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className={`cursor-pointer transition-all ${mode === 'caisse' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/20">
                      <ShoppingCart className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <h5 className="font-medium">Mode Caisse</h5>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Interface optimisée pour les ventes et la gestion de caisse
                  </p>
                  <Button 
                    variant={mode === 'caisse' ? 'default' : 'outline'} 
                    size="sm" 
                    className="w-full"
                    disabled={modeLoading || mode === 'caisse'}
                    onClick={() => handleModeChange('caisse')}
                  >
                    {mode === 'caisse' ? 'Mode actuel' : 'Passer en mode Caisse'}
                  </Button>
                </CardContent>
              </Card>

              <Card className={`cursor-pointer transition-all ${mode === 'retrait' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/20">
                      <Package className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <h5 className="font-medium">Mode Retrait</h5>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Interface spécialisée pour la gestion des retraits de stock
                  </p>
                  <Button 
                    variant={mode === 'retrait' ? 'default' : 'outline'} 
                    size="sm" 
                    className="w-full"
                    disabled={modeLoading || mode === 'retrait'}
                    onClick={() => handleModeChange('retrait')}
                  >
                    {mode === 'retrait' ? 'Mode actuel' : 'Passer en mode Retrait'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Paramètres de caisse - Seulement en mode caisse ou sans mode défini */}
        {userRole === 'admin' && !isRetraitMode && <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Caisse
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium">Affichage des prix</h4>
                  <p className="text-sm text-muted-foreground">
                    Contrôlez si les prix sont visibles dans l'interface de caisse
                  </p>
                </div>
                <ShowPricesToggle showLabel />
              </div>
            </CardContent>
          </Card>}

        {/* Sécurité et mot de passe */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Sécurité
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Changement de mot de passe */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Modifier le mot de passe
                </h4>
                <p className="text-sm text-muted-foreground">
                  Assurez-vous d'utiliser un mot de passe fort et unique
                </p>
              </div>
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="current-password">Mot de passe actuel</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Entrez votre mot de passe actuel"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Entrez votre nouveau mot de passe"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Confirmez votre nouveau mot de passe"
                  />
                </div>
                <Button 
                  onClick={handlePasswordChange}
                  disabled={isUpdatingPassword || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
                  className="w-fit"
                >
                  {isUpdatingPassword ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
                </Button>
              </div>
            </div>
            
            <Separator />
            
            {/* Paramètres de notifications */}
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium flex items-center gap-2">
                  <Bell className="w-4 h-4" />
                  Notifications
                </h4>
                <p className="text-sm text-muted-foreground">
                  Gérez vos préférences de notifications
                </p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email-notifications">Notifications par email</Label>
                    <p className="text-xs text-muted-foreground">Recevoir des notifications importantes par email</p>
                  </div>
                  <Switch 
                    id="email-notifications"
                    checked={settings.emailNotifications || false}
                    onCheckedChange={(checked) => updateSettings({ emailNotifications: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="sales-alerts">Alertes de ventes</Label>
                    <p className="text-xs text-muted-foreground">Être notifié des ventes importantes</p>
                  </div>
                  <Switch 
                    id="sales-alerts"
                    checked={settings.salesAlerts || false}
                    onCheckedChange={(checked) => updateSettings({ salesAlerts: checked })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="stock-alerts">Alertes de stock</Label>
                    <p className="text-xs text-muted-foreground">Être notifié quand le stock est bas</p>
                  </div>
                  <Switch 
                    id="stock-alerts"
                    checked={settings.stockAlerts || true}
                    onCheckedChange={(checked) => updateSettings({ stockAlerts: checked })}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Paramètres du profil */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Profil utilisateur
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div>
                <Label htmlFor="display-name">Nom d'affichage</Label>
                <Input
                  id="display-name"
                  value={profileForm.displayName}
                  onChange={(e) => setProfileForm(prev => ({ ...prev, displayName: e.target.value }))}
                  placeholder="Votre nom d'affichage"
                />
              </div>
              <div>
                <Label htmlFor="language">Langue</Label>
                <Select value={settings.language || 'fr'} onValueChange={(value) => updateSettings({ language: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une langue" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="sound-effects">Effets sonores</Label>
                  <p className="text-xs text-muted-foreground">Sons lors des actions (caisse, scanner)</p>
                </div>
                <Switch 
                  id="sound-effects"
                  checked={settings.soundEffects !== false}
                  onCheckedChange={(checked) => updateSettings({ soundEffects: checked })}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions de compte */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" />
              Données et compte
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium">Exporter mes données</h4>
                <p className="text-sm text-muted-foreground">
                  Télécharger une copie de vos données personnelles
                </p>
              </div>
              <Button variant="outline" onClick={handleExportData}>
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </div>
            
            <Separator />
            
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-medium text-destructive">Zone dangereuse</h4>
                <p className="text-sm text-muted-foreground">
                  Actions irréversibles sur votre compte
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer le compte
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action ne peut pas être annulée. Cela supprimera définitivement votre compte
                      et toutes vos données de nos serveurs.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAccount} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Supprimer définitivement
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>


      </div>
    </MainLayout>;
};